/**
 * The state and rules behind the item editor.
 *
 * Twenty pieces of state, the derived values they feed, and the save and offer
 * actions — kept out of the screen so the form is a rendering of one object
 * rather than a place where every field is declared again. The A/B against the
 * tabbed editor this replaced was possible only because both rendered from this
 * one controller; with that editor gone it remains the single place a field is
 * defined, so a control cannot write something the others do not read.
 *
 * The rules the controller applies when saving live in `item-payload.ts`; the
 * ones it shows the merchant while typing live in `item-form.ts`. This file is
 * the wiring between them and the screen.
 */

import { useCallback, useMemo, useState } from 'react';
import { formatZonedInput, parseMoneyInput, parseZonedInput, toMoneyInput } from '@yummy/shared';
import {
  createDiscount,
  createMenuItem,
  deleteDiscount,
  removeMedia,
  setAvailabilityWindows,
  setItemOptionGroups,
  updateMenuItem,
  uploadMedia,
  type AvailabilityWindowInput,
  type MerchantMenuItem,
} from '@yummy/db';
import { useAction, useLocale, useToast } from '@yummy/ui-rnr';

import { buildItemPayload } from './item-payload';
import { customerPaysCents } from './item-form';
import { supabase } from './supabase';

/** A group of extras while it is being edited: prices are typed text. */
export interface OptionGroupRow {
  name: string;
  priceMode: 'absolute' | 'delta';
  /** True when the customer may pick one only, rather than any number. */
  oneOnly: boolean;
  /**
   * The most they may pick, as typed, when `oneOnly` is false.
   *
   * Empty means all of them, resolved against the choices at save time. A number
   * cannot serve as the default: a new group has one blank row, so a cap written
   * when Any is chosen is 1 — and stays 1 as real choices are added, making Any
   * mean One. Blank follows the choices instead.
   */
  maxSelect: string;
  required: boolean;
  choices: OptionChoiceRow[];
}

export interface OptionChoiceRow {
  name: string;
  price: string;
}

/** What the screen hands the controller. Everything else is the controller's. */
export interface ItemFormInput {
  item: MerchantMenuItem | null;
  categories: Array<{ id: string; name: string }>;
  currency: string;
  restaurantId: string;
  timezone: string;
  defaultPrepMinutes: number;
  openTime: string;
  closeTime: string;
  onSaved: () => void;
  onDone: () => void;
}

/**
 * Everything the form renders from.
 *
 * Returned as one object so the form is a pure function of it — no component
 * reaches for a setter the others do not have, because they are the same ones.
 */
export type ItemFormController = ReturnType<typeof useItemForm>;

/**
 * Builds the shared item-editor controller.
 *
 * @param input The dish, the restaurant's settings, and the finish handlers.
 * @returns State, derived rules, and the save/offer actions.
 */
export function useItemForm({
  item,
  categories,
  currency,
  restaurantId,
  timezone,
  defaultPrepMinutes,
  openTime,
  closeTime,
  onSaved,
  onDone,
}: ItemFormInput) {
  const { t } = useLocale();
  const toast = useToast();
  const action = useAction();

  const [name, setName] = useState(item?.name ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [price, setPrice] = useState(toMoneyInput(item?.price_cents ?? 0));
  // Groups of extras: sugar and ice, or toppings on a pizza. Held as the form
  // holds them — prices as typed text — and replaced wholesale on save.
  const [groups, setGroups] = useState<OptionGroupRow[]>(() =>
    (item?.option_groups ?? []).map((group) => ({
      name: group.name,
      priceMode: group.price_mode,
      // A group capped at one is the merchant's "pick one", and it is the only
      // record of that intent — max_select is where it lives.
      oneOnly: group.max_select === 1,
      /**
       * A cap equal to the choices is how "all of them" is stored, the column
       * holding only a number. Read back as blank, it keeps meaning all — so
       * adding a choice later carries the cap up with it. Read back as a number,
       * it becomes a ceiling the merchant never set: four choices, cap three.
       *
       * A cap below the count is a real choice and is kept as one.
       */
      maxSelect: group.max_select === group.choices.length ? '' : String(group.max_select),
      required: group.min_select > 0,
      choices: group.choices.map((choice) => ({
        name: choice.name,
        price: toMoneyInput(choice.price_cents),
      })),
    })),
  );
  const [prepMinutes, setPrepMinutes] = useState(String(item?.prep_minutes_baseline ?? 0));
  // Dumplings come in fives; 1 means no minimum at all.
  const [minQuantity, setMinQuantity] = useState(String(item?.min_quantity ?? 1));
  const [trackInventory, setTrackInventory] = useState(item?.track_inventory ?? false);
  const [inventoryCount, setInventoryCount] = useState(String(item?.inventory_count ?? 0));
  const [isActive, setIsActive] = useState(item?.is_active ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [pendingPhoto, setPendingPhoto] = useState<{ file: File | Blob; previewUrl: string } | null>(
    null
  );
  const [removePhoto, setRemovePhoto] = useState(false);

  // The category this was opened from, if any. Read straight from the URL so it
  // does not depend on which component the back handler lives in.
  const openedFromCategory = new URLSearchParams(window.location.search).get('category');
  const [categoryId, setCategoryId] = useState<string | null>(
    item?.category_id ?? openedFromCategory ?? categories[0]?.id ?? null,
  );

  // Availability windows, held as one list and saved as a set.
  const [windows, setWindows] = useState<AvailabilityWindowInput[]>(
    item?.availability_windows.map((w) => ({
      weekday: w.weekday,
      start_time: w.start_time.slice(0, 5),
      end_time: w.end_time.slice(0, 5),
    })) ?? [],
  );

  // New discount form.
  const [discountKind, setDiscountKind] = useState<'percent' | 'amount'>('percent');
  const [discountValue, setDiscountValue] = useState('10');
  const [discountLabel, setDiscountLabel] = useState('');
  // The offer window is a wall clock at the shop, so the defaults are read there
  // too: "starts now" is now, but it has to be *written down* in the zone the
  // field means, or a merchant abroad would open the form on the wrong evening.
  const [discountStart, setDiscountStart] = useState(() => formatZonedInput(new Date(), timezone));
  const [discountEnd, setDiscountEnd] = useState(() =>
    formatZonedInput(new Date(Date.now() + 4 * 3_600_000), timezone),
  );

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  /** True when a group sets the price, which makes the dish's own price moot. */
  const pricedByGroup = groups.some((group) => group.priceMode === 'absolute');

  /** The cheapest choice of the price-setting groups, for showing in the field. */
  const cheapestGroupPrice = (() => {
    const prices = groups
      .filter((group) => group.priceMode === 'absolute')
      .flatMap((group) => group.choices.map((choice) => parseMoneyInput(choice.price)))
      .filter((value): value is number => value !== null);
    return prices.length > 0 ? Math.min(...prices) : 0;
  })();

  /**
   * What a group is for.
   *
   * Two answers, not three. Whether a group's choices are free or cost extra is
   * already in the prices — all zero or not — so a third state for it was a
   * control that could not be moved: both "free" and "adds" are a delta, and
   * clicking "adds" on an all-zero group changed nothing, so it snapped back.
   *
   * What the prices cannot say is whether the choices *are* the dish's price.
   * That is the question this asks.
   */
  const groupIntent = (group: OptionGroupRow): 'extras' | 'sets' =>
    group.priceMode === 'absolute' ? 'sets' : 'extras';

  /** The cheapest price this group sets, for saying what it does to the dish. */
  const cheapestOfGroup = (group: OptionGroupRow) => {
    const prices = group.choices
      .map((choice) => parseMoneyInput(choice.price))
      .filter((cents): cents is number => cents !== null);
    return prices.length > 0 ? Math.min(...prices) : 0;
  };

  /**
   * A group the merchant has started but not named.
   *
   * The save drops these — the writer keys groups by name, and the form filters
   * the nameless ones out — so it happened silently: choices typed, save
   * pressed, nothing saved and nothing said.
   */
  const groupNeedsName = (group: OptionGroupRow) =>
    group.name.trim() === '' && group.choices.some((choice) => choice.name.trim() !== '');

  /**
   * A cap larger than the choices it caps.
   *
   * Saving clamped it to the number of choices, which is the right result arrived
   * at silently — the number you typed changed under you with nothing to say why.
   * Refused instead, the same as a group with no name.
   */
  const groupCapTooHigh = (group: OptionGroupRow) => {
    if (group.oneOnly || group.priceMode === 'absolute') return false;
    const cap = Math.trunc(Number(group.maxSelect));
    const choices = group.choices.filter((choice) => choice.name.trim() !== '').length;
    return Number.isFinite(cap) && cap > choices;
  };

  /**
   * A name two choices share, or null.
   *
   * Not refused: the same name at two prices — a small and a large "extra
   * cheese" — is a real thing to sell. But the customer sees two identical
   * chips and the kitchen sees the name twice with nothing to tell them apart,
   * so it is worth saying out loud.
   */
  const groupDuplicateName = (group: OptionGroupRow) => {
    const seen = new Set<string>();
    for (const choice of group.choices) {
      const name = choice.name.trim();
      if (name === '') continue;
      const key = name.toLowerCase();
      if (seen.has(key)) return name;
      seen.add(key);
    }
    return null;
  };

  const groupIsFree = (group: OptionGroupRow) =>
    group.priceMode === 'absolute' &&
    group.choices.length > 0 &&
    group.choices.every((choice) => (parseMoneyInput(choice.price) ?? 0) === 0);

  const effectivePriceCents = parseMoneyInput(price);

  /** What the customer pays while the offer runs, shown as the fields change. */
  const offerPriceCents = customerPaysCents(effectivePriceCents, discountKind, discountValue);

  const title = item ? item.name : t('merchant.menu.newItem');

  // ---------------------------------------------------------------------------
  // Group editing
  // ---------------------------------------------------------------------------

  const updateGroup = (index: number, patch: Partial<OptionGroupRow>) =>
    setGroups((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const updateChoice = (groupIndex: number, choiceIndex: number, patch: Partial<OptionChoiceRow>) =>
    setGroups((rows) =>
      rows.map((row, i) =>
        i === groupIndex
          ? { ...row, choices: row.choices.map((c, j) => (j === choiceIndex ? { ...c, ...patch } : c)) }
          : row
      )
    );
  const removeGroup = (index: number) => setGroups((rows) => rows.filter((_, i) => i !== index));
  // Functional updates, so these do not have to close over `groups`: a stable
  // identity is what lets the returned controller be memoised honestly.
  const addChoice = useCallback(
    (groupIndex: number) =>
      setGroups((rows) =>
        rows.map((row, i) =>
          i === groupIndex ? { ...row, choices: [...row.choices, { name: '', price: '' }] } : row
        )
      ),
    []
  );
  const removeChoice = useCallback(
    (groupIndex: number, choiceIndex: number) =>
      setGroups((rows) =>
        rows.map((row, i) =>
          i === groupIndex
            ? { ...row, choices: row.choices.filter((_, j) => j !== choiceIndex) }
            : row
        )
      ),
    []
  );
  const addGroup = () =>
    setGroups((rows) => [
      ...rows,
      {
        name: '',
        priceMode: 'delta',
        oneOnly: true,
        maxSelect: '',
        required: false,
        choices: [{ name: '', price: '' }],
      },
    ]);

  // ---------------------------------------------------------------------------
  // Availability editing
  // ---------------------------------------------------------------------------

  const addWindow = useCallback(
    () =>
      setWindows((rows) => [
        ...rows,
        {
          weekday: rows.length > 0 ? (rows[rows.length - 1]!.weekday + 1) % 7 : 0,
          start_time: openTime.slice(0, 5),
          end_time: closeTime.slice(0, 5),
        },
      ]),
    [openTime, closeTime]
  );
  const updateWindow = (index: number, patch: Partial<AvailabilityWindowInput>) =>
    setWindows((rows) => rows.map((w, i) => (i === index ? { ...w, ...patch } : w)));
  const removeWindow = (index: number) =>
    setWindows((rows) => rows.filter((_, i) => i !== index));
  const copyToEveryDay = () =>
    setWindows((rows) => {
      const first = rows[0];
      if (!first) return rows;
      return Array.from({ length: 7 }, (_, weekday) => ({
        weekday,
        start_time: first.start_time,
        end_time: first.end_time,
      }));
    });
  const clearAllWindows = () => setWindows([]);

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  /**
   * Validates and writes the dish.
   *
   * @returns False when validation refused the save, so the caller can bring the
   *   offending tab into view; true when the write was attempted.
   */
  const save = useCallback(async (): Promise<boolean> => {
    const priceCents = parseMoneyInput(price);
    const nextErrors: Record<string, string> = {};

    if (!name.trim()) nextErrors.name = t('merchant.menu.nameRequired');
    // Refused rather than dropped, so the merchant is told instead of finding
    // the group missing later.
    if (groups.some(groupNeedsName)) {
      nextErrors.options = t('merchant.menu.extrasNameRequired');
    }
    if (groups.some(groupCapTooHigh)) {
      nextErrors.options = t('merchant.menu.extrasUpToTooHigh');
    }
    // A group that sets the price owns it, so the dish's own price is not asked
    // for; otherwise it is required as it always was.
    if (!pricedByGroup && priceCents === null) {
      nextErrors.price = t('merchant.menu.priceRequired');
    }
    if (trackInventory && !Number.isFinite(Number(inventoryCount))) {
      nextErrors.inventory = t('merchant.menu.inventoryRequired');
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return false;
    }
    setErrors({});

    const saved = await action.run(async () => {
      // The photo uploads first: a row pointing at a failed upload would render
      // as a broken image on the customer menu.
      let photoUrl = item?.photo_url ?? null;
      let photoPath = item?.photo_path ?? null;

      if (pendingPhoto) {
        const uploaded = await uploadMedia(supabase, restaurantId, pendingPhoto.file, 'menu');
        if (item?.photo_path) await removeMedia(supabase, item.photo_path).catch(() => undefined);
        photoUrl = uploaded.publicUrl;
        photoPath = uploaded.path;
      } else if (removePhoto && item?.photo_path) {
        await removeMedia(supabase, item.photo_path).catch(() => undefined);
        photoUrl = null;
        photoPath = null;
      }

      // Built by a pure function, so what a form writes is provable without a
      // browser: the same edits produce the same row, or the difference is the
      // bug.
      const payload = buildItemPayload({
        categoryId,
        name,
        description,
        priceCents,
        pricedByGroup,
        cheapestGroupPrice,
        photoUrl,
        photoPath,
        isActive,
        trackInventory,
        inventoryCount,
        prepMinutes,
        minQuantity,
      });

      const row = item
        ? await updateMenuItem(supabase, item.id, payload)
        : await createMenuItem(supabase, restaurantId, payload);

      await setAvailabilityWindows(supabase, row.id, windows);

      // Groups are replaced wholesale, so a group removed here is removed there.
      // A choice with no name is a half-typed row and is dropped; a group with no
      // usable choices is not written at all.
      await setItemOptionGroups(
        supabase,
        row.id,
        restaurantId,
        groups
          .filter((group) => group.name.trim() !== '')
          .map((group) => {
            const choices = group.choices
              .filter((choice) => choice.name.trim() !== '')
              .map((choice) => ({ name: choice.name, price_cents: parseMoneyInput(choice.price) ?? 0 }));
            // An absolute group is the dish's price, so exactly one choice sets
            // it. Otherwise the merchant decides, and "any" means as many as the
            // group holds — the server caps it there.
            const oneOnly = group.priceMode === 'absolute' || group.oneOnly;
            // "Any" defaults to all of them, which is what it always meant; a
            // number lower than that is the cap the merchant typed.
            const cap = Math.max(
              1,
              Math.min(choices.length, Math.trunc(Number(group.maxSelect)) || choices.length),
            );
            return {
              name: group.name,
              price_mode: group.priceMode,
              min_select: group.priceMode === 'absolute' || group.required ? 1 : 0,
              max_select: oneOnly ? 1 : cap,
              choices,
            };
          })
          .filter((group) => group.choices.length > 0)
      );
      return row;
    });

    if (saved) {
      toast.show(t('merchant.menu.saved', { name: saved.name }), { tone: 'success' });
      // Saving is finished with the form; scheduling an offer is not, so the two
      // outcomes part company here.
      onDone();
      return true;
    }
    return true;
  }, [
    price,
    name,
    trackInventory,
    inventoryCount,
    item,
    pendingPhoto,
    removePhoto,
    categoryId,
    description,
    isActive,
    prepMinutes,
    minQuantity,
    windows,
    pricedByGroup,
    cheapestGroupPrice,
    groups,
    restaurantId,
    action,
    toast,
    onDone,
    t,
  ]);

  // ---------------------------------------------------------------------------
  // Discounts
  // ---------------------------------------------------------------------------

  const addDiscount = useCallback(async () => {
    if (!item) return;
    const value =
      discountKind === 'percent' ? Math.trunc(Number(discountValue)) : parseMoneyInput(discountValue);
    if (value === null || !Number.isFinite(value) || value <= 0) {
      setErrors({ discount: t('merchant.menu.invalidDiscount') });
      return;
    }
    if (discountKind === 'percent' && value > 100) {
      setErrors({ discount: t('merchant.menu.percentTooHigh') });
      return;
    }
    // Read in the restaurant's zone, never the device's: `new Date(discountStart)`
    // would answer "18:00 where I am", and the shop's offer is "18:00 here".
    const startsAt = parseZonedInput(discountStart, timezone);
    const endsAt = parseZonedInput(discountEnd, timezone);
    if (startsAt === null || endsAt === null || !(endsAt > startsAt)) {
      setErrors({ discount: t('merchant.menu.offerEndBeforeStart') });
      return;
    }
    setErrors({});

    const created = await action.run(() =>
      createDiscount(supabase, restaurantId, item.id, {
        label: discountLabel.trim() || null,
        kind: discountKind,
        value,
        starts_at: startsAt.toISOString(),
        ends_at: endsAt.toISOString(),
        is_active: true,
      }),
    );

    if (created) {
      toast.show(t('merchant.menu.offerScheduled'), { tone: 'success' });
      onSaved();
    }
  }, [
    item,
    discountKind,
    discountValue,
    discountLabel,
    discountStart,
    discountEnd,
    // The zone the two wall times above are read in. It changes with the
    // restaurant, and a stale one would file the window against another shop's
    // clock — which is the bug this callback now avoids.
    timezone,
    action,
    restaurantId,
    toast,
    onSaved,
    t,
  ]);

  const removeDiscount = useCallback(
    async (id: string) => {
      const result = await action.run(() => deleteDiscount(supabase, id));
      if (!result && !action.error) onSaved();
    },
    [action, onSaved],
  );

  /** The photo shown in the picker, honouring a pending pick or removal. */
  const photoUrl = removePhoto ? null : (pendingPhoto?.previewUrl ?? item?.photo_url ?? null);

  const pickPhoto = (image: { file: File | Blob; previewUrl: string }) => {
    setPendingPhoto({ file: image.file, previewUrl: image.previewUrl });
    setRemovePhoto(false);
  };

  const toggleRemovePhoto = () => setRemovePhoto((value) => !value);

  return useMemo(
    () => ({
      // The dish
      item,
      isNew: item === null,
      title,
      currency,
      timezone,
      defaultPrepMinutes,
      categories,

      name,
      setName,
      description,
      setDescription,
      price,
      setPrice,

      // Choices
      groups,
      updateGroup,
      updateChoice,
      removeGroup,
      addChoice,
      removeChoice,
      addGroup,
      groupIntent,
      cheapestOfGroup,
      groupNeedsName,
      groupCapTooHigh,
      groupDuplicateName,
      groupIsFree,

      // Price consequence
      pricedByGroup,
      cheapestGroupPrice,
      effectivePriceCents,

      // Kitchen
      prepMinutes,
      setPrepMinutes,
      minQuantity,
      setMinQuantity,

      // Selling
      categoryId,
      setCategoryId,
      isActive,
      setIsActive,
      trackInventory,
      setTrackInventory,
      inventoryCount,
      setInventoryCount,

      // Photo
      photoUrl,
      pendingPhoto,
      removePhoto,
      pickPhoto,
      toggleRemovePhoto,

      // Availability
      windows,
      addWindow,
      updateWindow,
      removeWindow,
      copyToEveryDay,
      clearAllWindows,

      // Offer
      discountKind,
      setDiscountKind,
      discountValue,
      setDiscountValue,
      discountLabel,
      setDiscountLabel,
      discountStart,
      setDiscountStart,
      discountEnd,
      setDiscountEnd,
      offerPriceCents,
      addDiscount,
      removeDiscount,

      // Save
      save,
      errors,
      saving: action.pending,
      actionError: action.error,
      onDone,
    }),
    [
      item,
      title,
      currency,
      timezone,
      defaultPrepMinutes,
      categories,
      name,
      description,
      price,
      groups,
      addChoice,
      removeChoice,
      addDiscount,
      removeDiscount,
      addWindow,
      pricedByGroup,
      cheapestGroupPrice,
      effectivePriceCents,
      prepMinutes,
      minQuantity,
      categoryId,
      isActive,
      trackInventory,
      inventoryCount,
      photoUrl,
      pendingPhoto,
      removePhoto,
      windows,
      discountKind,
      discountValue,
      discountLabel,
      discountStart,
      discountEnd,
      offerPriceCents,
      save,
      errors,
      action.pending,
      action.error,
      onDone,
    ],
  );
}

